import { useSyncExternalStore } from 'react';

/**
 * Limbi de MENIU. Trebuie sincronizat cu `UiLanguage` din backend
 * (apps/dermato-service/src/template-placeholders/template-placeholders.ts).
 */
export const SUPPORTED_LANGUAGES = ['ro', 'en'] as const;
export type UiLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Alias păstrat pentru compatibilitate: vechiul `SupportedLanguage` a fost
 * limba de document, dar consumatorii lui din frontend erau, de fapt,
 * consumatori de limba de MENIU. Se păstrează ca alias al lui `UiLanguage`.
 */
export type SupportedLanguage = UiLanguage;

export const LANGUAGE_LABELS: Record<UiLanguage, { short: string; native: string; flag: string }> = {
  ro: { short: 'RO', native: 'Română', flag: '🇷🇴' },
  en: { short: 'EN', native: 'English', flag: '🇬🇧' },
};

/**
 * Limbi de DOCUMENT. Sincronizat cu `DocumentLanguage` din backend.
 * SRB e disponibil la nivel de limbă de document (nu de meniu).
 */
export const SUPPORTED_DOCUMENT_LANGUAGES = ['ro', 'en', 'srb'] as const;
export type DocumentLanguage = typeof SUPPORTED_DOCUMENT_LANGUAGES[number];

export const DOCUMENT_LANGUAGE_LABELS: Record<DocumentLanguage, { short: string; native: string; flag: string }> = {
  ro: { short: 'RO', native: 'Română', flag: '🇷🇴' },
  en: { short: 'EN', native: 'English', flag: '🇬🇧' },
  srb: { short: 'SRB', native: 'Srpski', flag: '🇷🇸' },
};

/**
 * Indicator discret pentru limbile de document care încă nu au traducere
 * validată clinic (D10). Nu ascunde limba — doar avertizează utilizatorul.
 */
export const DOCUMENT_LANGUAGE_HINTS: Partial<Record<DocumentLanguage, string>> = {
  srb: 'SRB — în traducere: până la sosirea șablonului validat, se generează versiunea în română.',
};

const STORAGE_KEY = 'hepaskin-language';
const DEFAULT_LANGUAGE: UiLanguage = 'ro';

/** Validare pentru limba de MENIU. */
export const isSupported = (value: string | null): value is UiLanguage =>
  !!value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/** Validare pentru limba de DOCUMENT (RO / EN / SRB). */
export const isDocumentLanguage = (value: string | null | undefined): value is DocumentLanguage =>
  !!value && (SUPPORTED_DOCUMENT_LANGUAGES as readonly string[]).includes(value);

const readStored = (): UiLanguage => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isSupported(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

/**
 * Hook global pentru limba activă a MENIULUI. Persistă în localStorage.
 * Orice component poate consuma limba curentă sau o poate schimba.
 */
let activeLanguage = readStored();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const onStorage = (event: StorageEvent) => {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  activeLanguage = readStored();
  notify();
};

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
};

const setLanguage = (language: UiLanguage) => {
  activeLanguage = language;
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Keep all consumers synchronized even when persistence is unavailable.
  }
  notify();
};

export const useLanguage = (): {
  language: UiLanguage;
  setLanguage: (lang: UiLanguage) => void;
} => {
  const language = useSyncExternalStore(subscribe, () => activeLanguage, () => DEFAULT_LANGUAGE);

  return {
    language,
    setLanguage,
  };
};
